
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Search, ArrowRight, Plus, Edit, Trash2, Settings } from "lucide-react";
import * as LucideIcons from "lucide-react";
import RightSideMenu from "@/components/navigation/RightSideMenu";
import AdPanel from "@/components/ads/AdPanel";
import KinkipediaHeader from "@/components/kinkipedia/KinkipediaHeader";
import TopicEditor from "@/components/admin/TopicEditor";
import KinkipediaAdmin from "@/components/admin/KinkipediaAdmin";
import { AlphaLoader } from "@/components/ui/alpha-loader";
import { useLanguage } from "@/hooks/useLanguage";
import { useAdminRole } from "@/hooks/useAdminRole";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Topic {
  id: number;
  name: string;
  description: string | null;
  icon_name: string | null;
  color_class: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const Kinkipedia = () => {
  const { isRTL } = useLanguage();
  const { isAdmin, loading: adminLoading } = useAdminRole();
  const [searchTerm, setSearchTerm] = useState("");
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTopic, setEditingTopic] = useState<Topic | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const fetchTopics = async () => {
    try {
      const { data, error } = await supabase
        .from('kinkipedia_topics')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setTopics(data || []);
    } catch (error) {
      console.error('Error fetching topics:', error);
      toast({
        title: "Error",
        description: "Failed to fetch topics",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTopics();
  }, []);

  const deleteTopic = async (topicId: number) => {
    try {
      const { error } = await supabase
        .from('kinkipedia_topics')
        .delete()
        .eq('id', topicId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Topic deleted successfully",
      });
      
      fetchTopics();
    } catch (error) {
      console.error('Error deleting topic:', error);
      toast({
        title: "Error",
        description: "Failed to delete topic",
        variant: "destructive",
      });
    }
  };

  const handleTopicSaved = () => {
    setEditingTopic(null);
    setShowCreateDialog(false);
    fetchTopics();
  };

  const filteredTopics = topics.filter(topic =>
    topic.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (topic.description && topic.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleTopicClick = (topicId: number) => {
    navigate(`/kinkipedia/topic/${topicId}`);
  };

  if (loading || adminLoading) {
    return (
      <div className="min-h-screen bg-carbon flex items-center justify-center">
        <AlphaLoader size="large" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-carbon flex flex-col ${isRTL ? 'rtl' : 'ltr'}`}>
      <AdPanel />
      <RightSideMenu />
      
      <div className="flex-1 p-4 pb-20">
        <KinkipediaHeader />

        {/* Admin Controls */}
        {isAdmin && (
          <div className="mb-6 flex flex-wrap gap-3">
            <Dialog open={showAdminPanel} onOpenChange={setShowAdminPanel}>
              <DialogTrigger asChild>
                <Button className="bg-neon-red hover:bg-neon-red/80 text-white">
                  <Settings className="w-4 h-4 mr-2" />
                  Admin Panel
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-6xl max-h-[90vh] overflow-auto">
                <DialogHeader>
                  <DialogTitle>Kinkipedia Administration</DialogTitle>
                  <DialogDescription>
                    Manage articles and content
                  </DialogDescription>
                </DialogHeader>
                <KinkipediaAdmin />
              </DialogContent>
            </Dialog>

            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-toxic-green/30 text-toxic-green hover:bg-toxic-green/20">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Topic
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
                <DialogHeader>
                  <DialogTitle>Create New Topic</DialogTitle>
                  <DialogDescription>
                    Add a new topic to Kinkipedia
                  </DialogDescription>
                </DialogHeader>
                <TopicEditor onSave={handleTopicSaved} onCancel={() => setShowCreateDialog(false)} />
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ice-white/40 w-5 h-5" />
            <Input
              placeholder="Search topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-carbon/50 border-ice-white/20 text-ice-white placeholder-ice-white/40 focus:border-neon-red/50"
            />
          </div>
        </div>

        {/* Topic Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredTopics.map((topic) => {
            const IconComponent = topic.icon_name ? (LucideIcons as any)[topic.icon_name] || LucideIcons.BookOpen : LucideIcons.BookOpen;
            return (
              <Card 
                key={topic.id} 
                className={`${topic.color_class || 'bg-carbon/80 border-ice-white/20'} border text-ice-white cursor-pointer hover:scale-105 transition-all duration-300 hover:shadow-neon-red/20 relative group`}
              >
                {/* Admin Controls */}
                {isAdmin && (
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <Dialog open={editingTopic?.id === topic.id} onOpenChange={(open) => !open && setEditingTopic(null)}>
                      <DialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingTopic(topic);
                          }}
                          className="h-8 w-8 p-0 bg-carbon/50 hover:bg-neon-red/20"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
                        <DialogHeader>
                          <DialogTitle>Edit Topic</DialogTitle>
                          <DialogDescription>
                            Modify the topic settings
                          </DialogDescription>
                        </DialogHeader>
                        {editingTopic && (
                          <TopicEditor 
                            topic={editingTopic} 
                            onSave={handleTopicSaved} 
                            onCancel={() => setEditingTopic(null)}
                          />
                        )}
                      </DialogContent>
                    </Dialog>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => e.stopPropagation()}
                          className="h-8 w-8 p-0 bg-carbon/50 hover:bg-red-500/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Topic</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{topic.name}"? This action cannot be undone and will affect all articles in this topic.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteTopic(topic.id)}
                            className="bg-destructive hover:bg-destructive/80"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}

                <div onClick={() => handleTopicClick(topic.id)}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <IconComponent className="w-8 h-8 text-toxic-green" />
                      <ArrowRight className="w-5 h-5 text-ice-white/60" />
                    </div>
                    <CardTitle className="text-lg leading-tight text-ice-white">{topic.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-ice-white/70 text-sm leading-relaxed mb-3">
                      {topic.description || "No description available"}
                    </p>
                    <Badge variant="outline" className="bg-carbon/30 text-ice-white border-ice-white/30 text-xs hover:bg-neon-red/20 hover:border-neon-red/50">
                      Click to explore
                    </Badge>
                  </CardContent>
                </div>
              </Card>
            );
          })}
        </div>

        {filteredTopics.length === 0 && (
          <div className="text-center py-12">
            <Search className="w-16 h-16 text-ice-white/30 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-ice-white/70 mb-2">No topics found</h3>
            <p className="text-ice-white/50">Try adjusting your search terms</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Kinkipedia;
